import { fileTypeFromBlob } from "file-type";

export const allowedTypes = [
  { ext: ".png", mime: "image/png" },
  { ext: ".jpg", mime: "image/jpeg" },
  { ext: ".jpeg", mime: "image/jpeg" },
  { ext: ".gif", mime: "image/gif" },
  { ext: ".pdf", mime: "application/pdf" },
  { ext: ".zip", mime: "application/zip" },
  { ext: ".json", mime: "application/json" },
  { ext: ".txt", mime: "text/plain" },
  { ext: ".doc", mime: "application/msword" },
  { ext: ".html", mime: "text/html" },
  { ext: ".htm", mime: "text/html" },
  {
    ext: ".docx",
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  { ext: ".mp3", mime: "audio/mpeg" },
  { ext: ".mp4", mime: "video/mp4" },
];

export const restrictedTypes = [
  { ext: ".exe", mime: "application/x-msdownload" },
  { ext: ".com", mime: "application/x-msdos-program" },
  { ext: ".sh", mime: "application/x-sh" },
  { ext: ".bat", mime: "application/x-bat" },
  { ext: ".js", mime: "application/javascript" },
  { ext: ".msi", mime: "application/x-msi" },
  { ext: ".ps1", mime: "application/x-powershell" },
  { ext: ".py", mime: "application/x-python" },
  { ext: ".hta", mime: "application/x-hta" },
  { ext: ".jar", mime: "application/java-archive" },
  { ext: ".dll", mime: "application/x-dosexec" },
  { ext: ".pl", mime: "application/x-perl" },
  { ext: ".php", mime: "application/x-php" },
  { ext: ".rb", mime: "application/x-ruby" },
  { ext: ".apk", mime: "application/vnd.android.package-archive" },
];

// Utility: check if HTML is real
async function isHtmlContent(file) {
  const text = (await file.text()).trim().toLowerCase();
  return text.startsWith("<!doctype") || text.startsWith("<html");
}

// Utility: check if JSON is valid
async function isJsonContent(file) {
  try {
    JSON.parse(await file.text());
    return true;
  } catch {
    return false;
  }
}

export function useFileValidator() {
  const validateFiles = async (files) => {
    const errors = [];

    for (const file of files) {
      const fileName = file.name.toLowerCase();
      const fileParts = fileName.split(".");
      const fileExt = "." + fileParts.pop();

      // 1️⃣ Double-extension attack detection
      if (fileParts.length > 1) {
        const secondLastExt = "." + fileParts.pop();
        if (restrictedTypes.some((t) => t.ext === secondLastExt)) {
          errors.push(
            `${file.name} is not allowed: Suspicious double extension.`
          );
          continue;
        }
      }

      // 2️⃣ Check allowed/restricted extensions
      const allowed = allowedTypes.find((t) => t.ext === fileExt);
      const restricted = restrictedTypes.find((t) => t.ext === fileExt);

      if (restricted) {
        errors.push(`${file.name} is not allowed: Restricted file extension.`);
        continue;
      }
      if (!allowed) {
        errors.push(
          `${file.name} is not allowed: Extension not in allowed list.`
        );
        continue;
      }

      // 3️⃣ Detect file type
      const detected = await fileTypeFromBlob(file);

      if (detected) {
        // Binary or detectable type
        const detectedExt = "." + detected.ext;
        const detectedMime = detected.mime;

        if (fileExt !== detectedExt || detectedMime !== allowed.mime) {
          errors.push(
            `${file.name} is not allowed: File extension/MIME does not match real file content (expected ${allowed.ext} / ${allowed.mime}).`
          );
          continue;
        }
      } else {
        // Text-based files (no signature)
        if (fileExt === ".html" || fileExt === ".htm") {
          const validHtml = await isHtmlContent(file);
          if (!validHtml) {
            errors.push(`${file.name} is not allowed: Not a valid HTML file.`);
            continue;
          }
        } else if (fileExt === ".json") {
          const validJson = await isJsonContent(file);
          if (!validJson) {
            errors.push(`${file.name} is not allowed: Invalid JSON content.`);
            continue;
          }
        } else if (fileExt === ".txt") {
          const text = (await file.text()).toLowerCase();
          // optional: reject TXT if it contains HTML or JS
          if (text.includes("<html") || text.includes("<script")) {
            errors.push(
              `${file.name} is not allowed: TXT contains HTML/JS code.`
            );
            continue;
          }
        } else {
          // ❌ Reject if we cannot verify content
          errors.push(
            `${file.name} is not allowed: Could not verify file content.`
          );
          continue;
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  };

  return { validateFiles };
}
